import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight, RotateCcw, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "cs_assistant_session";
const SERVICE_MARKER = /\[\[SERVICE:([a-z0-9-]+)\]\]/i;

const ROUTE_BY_SLUG = {
  "creation-support": { label: "Ouvrir « Créer mon support »", route: "/configurer/creation" },
  "fichier-conforme": { label: "Ouvrir « Rendre mon fichier conforme »", route: "/configurer/conforme" },
  "edition": { label: "Ouvrir « Mise en page & édition »", route: "/mise-en-page" },
  "planche-pdf-prete": { label: "Ouvrir « Planche PDF prête »", route: "/configurer/planche" },
  "adapter-format": { label: "Ouvrir « Adapter le format »", route: "/configurer/format" },
  "premiere-de-couverture": { label: "Voir « Première de couverture »", route: "/prestation/premiere-de-couverture" },
  "couverture-complete": { label: "Voir « Couverture complète »", route: "/prestation/couverture-complete" },
  "adapter-couverture": { label: "Voir « Adapter une couverture »", route: "/prestation/adapter-couverture" },
  "recomposition-graphique": { label: "Voir « Recomposition graphique »", route: "/prestation/recomposition-graphique" },
  "controle-prepresse": { label: "Voir « Contrôle prépresse »", route: "/prestation/controle-prepresse" },
  "devis": { label: "Demander un devis personnalisé", route: "/demande-devis" },
};

function parseRecommendation(text) {
  if (!text) return { clean: "", slug: null };
  const match = text.match(SERVICE_MARKER);
  if (!match) return { clean: text.trim(), slug: null };
  const slug = match[1].toLowerCase();
  const clean = text.replace(SERVICE_MARKER, "").trim();
  return { clean, slug };
}

function Bubble({ role, children, testId }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={testId}>
      <div
        className={`max-w-[85%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-black text-[#FAF8F5] border border-black"
            : "bg-white text-black border border-black/15"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function ChooseHelp() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Recover past conversation on mount
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const r = await fetch(`${API}/assistant/history/${sessionId}`);
        if (r.ok) {
          const data = await r.json();
          setMessages(data.messages || []);
        }
      } catch (_e) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamBuf]);

  const latestReco = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        return parseRecommendation(messages[i].content);
      }
    }
    return { clean: "", slug: null };
  }, [messages]);

  async function send(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || streaming) return;
    setError("");
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setStreaming(true);
    setStreamBuf("");

    try {
      const resp = await fetch(`${API}/assistant/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ session_id: sessionId || null, message: text }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`Erreur ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const raw of chunks) {
          const lines = raw.split("\n");
          const evLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!evLine || !dataLine) continue;
          const evName = evLine.slice(6).trim();
          const payload = JSON.parse(dataLine.slice(5).trim());
          if (evName === "session" && payload.session_id) {
            setSessionId(payload.session_id);
            localStorage.setItem(STORAGE_KEY, payload.session_id);
          } else if (evName === "delta") {
            acc += payload.content || "";
            setStreamBuf(acc);
          } else if (evName === "error") {
            throw new Error(payload.detail || "Erreur inconnue");
          }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, created_at: new Date().toISOString() }]);
      setStreamBuf("");
    } catch (err) {
      setError(err.message || "Impossible de contacter le conseiller pour le moment.");
    } finally {
      setStreaming(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }

  async function reset() {
    if (streaming) return;
    try {
      await fetch(`${API}/assistant/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId || null }),
      });
    } catch (_e) { /* silent */ }
    localStorage.removeItem(STORAGE_KEY);
    setSessionId("");
    setMessages([]);
    setStreamBuf("");
    setError("");
    setInput("");
  }

  const streamPreview = streamBuf ? parseRecommendation(streamBuf).clean : "";
  const isEmpty = messages.length === 0 && !streaming;

  return (
    <div className="cs-cream min-h-[80vh] py-16" data-testid="page-choose-help">
      <div className="max-w-3xl mx-auto px-6">
        <div className="cs-eyebrow text-neutral-500">Assistant · Charbonier Studio</div>
        <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">Je ne sais pas quoi choisir.</h1>
        <p className="mt-4 text-neutral-700 max-w-xl">
          Décrivez votre projet en quelques mots. Le conseiller vous pose 1 à 3 questions puis oriente vers la prestation la plus adaptée du catalogue.
        </p>

        <div className="mt-10 cs-card p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles size={16} className="text-black" />
              <span className="font-mono uppercase tracking-widest text-xs text-neutral-500">Conseiller Charbonier</span>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                onClick={reset}
                disabled={streaming}
                className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-black inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="assistant-reset"
              >
                <RotateCcw size={13} /> Nouvelle conversation
              </button>
            )}
          </div>

          <div ref={scrollRef} className="px-6 py-6 space-y-4 max-h-[52vh] overflow-y-auto" data-testid="assistant-scroll">
            {isEmpty && (
              <div className="text-sm text-neutral-600 leading-relaxed">
                Bonjour, je suis le conseiller de Charbonier Studio. Dites-moi en une ou deux phrases sur quoi vous travaillez — un livre, un flyer, un fichier refusé par un imprimeur, une couverture… — et je vous oriente.
              </div>
            )}
            {messages.map((m, i) => {
              const { clean } = m.role === "assistant" ? parseRecommendation(m.content) : { clean: m.content };
              return (
                <Bubble key={i} role={m.role} testId={`assistant-msg-${i}-${m.role}`}>
                  {clean}
                </Bubble>
              );
            })}
            {streaming && streamPreview && (
              <Bubble role="assistant" testId="assistant-stream">
                {streamPreview}
                <span className="inline-block w-2 h-4 ml-1 bg-black animate-pulse align-middle" />
              </Bubble>
            )}
            {streaming && !streamPreview && (
              <div className="flex items-center gap-2 text-sm text-neutral-500" data-testid="assistant-thinking">
                <Loader2 size={14} className="animate-spin" /> Le conseiller réfléchit…
              </div>
            )}
          </div>

          {latestReco.slug && ROUTE_BY_SLUG[latestReco.slug] && !streaming && (
            <div className="px-6 py-5 border-t border-black/10 bg-black text-[#FAF8F5] flex flex-wrap items-center justify-between gap-4" data-testid="assistant-recommendation">
              <div className="text-sm">
                <div className="cs-eyebrow text-[#FAF8F5]/60">Prestation recommandée</div>
                <div className="mt-1 text-base">{ROUTE_BY_SLUG[latestReco.slug].label.replace(/^Ouvrir « |^Voir « |»/g, "")}</div>
              </div>
              <button
                type="button"
                onClick={() => navigate(ROUTE_BY_SLUG[latestReco.slug].route)}
                className="inline-flex items-center gap-2 bg-[#FAF8F5] text-black px-5 py-2.5 text-sm font-medium hover:opacity-90"
                data-testid="assistant-cta"
              >
                {ROUTE_BY_SLUG[latestReco.slug].label} <ArrowUpRight size={16} />
              </button>
            </div>
          )}

          <form onSubmit={send} className="px-6 py-5 border-t border-black/10 bg-white/50" data-testid="assistant-form">
            {error && <div className="text-sm text-red-700 mb-3" data-testid="assistant-error">{error}</div>}
            <div className="flex items-end gap-3">
              <textarea
                ref={inputRef}
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ex : j'ai un roman à mettre en page pour l'imprimeur…"
                className="cs-input flex-1 resize-none"
                disabled={streaming}
                data-testid="assistant-input"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                className="cs-btn-primary disabled:opacity-40"
                data-testid="assistant-send"
                aria-label="Envoyer le message"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <div className="mt-3 text-xs text-neutral-500 font-mono">
              Le conseiller répond instantanément. Vous pouvez toujours <Link to="/demande-devis" className="underline">demander un devis</Link> si votre projet est atypique.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
